import { ChevronLeft, ChevronRight } from "lucide-react";
import { calendarRows, dow, dm, addDays, todayISO, mondayOf, warnOf, projectLabel } from "@/lib/orders";
import { formatCZK } from "@/lib/format";
import { ModeIcon, orderAmount } from "./OrderBits";

const TONE = {
  shipped: "bg-brand/10 border-brand/30",
  confirmed: "bg-blue-500/10 border-blue-500/30",
  ordered: "bg-secondary border-border",
  ready: "bg-amber-500/15 border-amber-500/40",
  received: "bg-success/10 border-success/30 opacity-80",
};

function Chip({ o, onOpen }) {
  const tone = o.late ? "bg-destructive/10 border-destructive/40" : TONE[o.status] || TONE.ordered;
  return (
    <button type="button" onClick={() => onOpen(o)} data-testid={`cal-order-${o.id}`}
      className={`w-full text-left rounded-[10px] border px-2 py-1.5 hover:shadow-sm transition-shadow motion-reduce:transition-none ${tone}`}>
      <span className="flex items-center gap-1 text-[11px] text-muted-foreground min-w-0">
        <ModeIcon o={o} size={12} /><span className="truncate">{o.status === "ready" ? (o.delivery?.branch || "vyzvednout") : (o.due_note || o.delivery?.carrier || (o.status === "received" ? "převzato" : ""))}</span>
      </span>
      <b className="block text-[12.5px] font-bold truncate">{o.supplier_name}</b>
      <span className="block text-[11.5px] text-muted-foreground truncate">{o.title}</span>
    </button>
  );
}

const weekLabel = (mon) => {
  const sun = addDays(mon, 6);
  return mon.slice(5, 7) === sun.slice(5, 7) ? `${Number(mon.slice(8))}.–${dm(sun)}` : `${dm(mon)} – ${dm(sun)}`;
};

// Kalendář dodávek (PC): řádek = stavba, sloupec = den. Vpravo co řešit a co čeká na platbu.
export function OrderCalendar({ items, open, monday, onWeek, money, onOpen, am = "gross" }) {
  const amt = (o) => formatCZK(orderAmount(o, am === "both" ? "net" : am));   // 391: podle Zobrazení › Částky
  const today = todayISO();
  const { days, rows } = calendarRows(items, monday);
  const todo = open.filter((o) => o.bucket === "now");
  const got = open.filter((o) => o.bucket === "got");
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-4 items-start" data-testid="orders-calendar">
      <section className="glass rounded-[20px] overflow-hidden min-w-0">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border/70">
          <div className="inline-flex rounded-full border border-border bg-card overflow-hidden">
            <button type="button" onClick={() => onWeek(addDays(monday, -7))} aria-label="Předchozí týden" data-testid="cal-prev"
              className="w-10 h-9 grid place-items-center hover:bg-secondary"><ChevronLeft size={18} /></button>
            <span className="w-px bg-border" aria-hidden />
            <button type="button" onClick={() => onWeek(addDays(monday, 7))} aria-label="Další týden" data-testid="cal-next"
              className="w-10 h-9 grid place-items-center hover:bg-secondary"><ChevronRight size={18} /></button>
          </div>
          <b className="text-[15px] font-bold tabular-nums" data-testid="cal-week">{weekLabel(monday)}</b>
          {monday !== mondayOf(today) && (
            <button type="button" onClick={() => onWeek(mondayOf(today))} data-testid="cal-today"
              className="h-9 px-3.5 rounded-full border border-border bg-card text-[13px] font-bold hover:text-brand">Dnes</button>
          )}
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[860px] grid grid-cols-[180px_repeat(7,minmax(0,1fr))]">
            <div className="px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Stavba</div>
            {days.map((d) => (
              <div key={d} className={`px-2 py-2.5 text-[13px] border-l border-border/60 ${d === today ? "bg-brand/10 pp-ink font-bold" : "text-muted-foreground"}`}>
                {dow(d)} <span className="tabular-nums">{Number(d.slice(8))}.</span>
              </div>
            ))}
            {rows.map((r) => (
              <div key={r.key} className="contents" data-testid={`cal-row-${r.key}`}>
                <div className="px-3 py-2.5 border-t border-border/60 min-w-0">
                  <b className="block text-[13.5px] font-bold truncate">{r.name}</b>
                  {r.receiver && <span className="block text-[12px] text-muted-foreground truncate">{r.receiver}</span>}
                </div>
                {r.cells.map((list, i) => (
                  <div key={i} className={`p-1.5 border-t border-l border-border/60 space-y-1.5 min-w-0 ${days[i] === today ? "bg-brand/[.04]" : i > 4 ? "bg-secondary/30" : ""}`}>
                    {list.map((o) => <Chip key={o.id} o={o} onOpen={onOpen} />)}
                  </div>
                ))}
              </div>
            ))}
          </div>
          {!rows.length && <p className="px-4 py-10 text-center text-muted-foreground">Tenhle týden nemá žádná dodávka termín.</p>}
        </div>
        <div className="flex items-center gap-4 flex-wrap px-4 py-2.5 border-t border-border/70 text-[12px] text-muted-foreground">
          {[["bg-brand", "na cestě"], ["bg-blue-500", "potvrzený termín"], ["bg-amber-500", "k vyzvednutí na pobočce"], ["bg-destructive", "nedorazilo"]].map(([c, t]) => (
            <span key={t} className="inline-flex items-center gap-1.5"><i className={`w-2 h-2 rounded-full ${c}`} />{t}</span>
          ))}
        </div>
      </section>

      <aside className="space-y-3">
        <section className="glass rounded-[20px] p-3" data-testid="cal-todo">
          <h3 className="px-1 pb-2 text-[13px] font-extrabold text-destructive">Řešit {todo.length > 0 && <span className="tabular-nums">{todo.length}</span>}</h3>
          {!todo.length && <p className="px-1 pb-1 text-[13px] text-muted-foreground">Nic nehoří.</p>}
          <div className="space-y-2">
            {todo.map((o) => (
              <button key={o.id} type="button" onClick={() => onOpen(o)} className="w-full text-left bg-card rounded-[14px] border border-border/70 p-3 hover:shadow-sm">
                <span className={`text-[11.5px] font-bold ${warnOf(o, today)?.tone === "red" ? "text-destructive" : "text-amber-700 dark:text-amber-400"}`}>{warnOf(o, today)?.text}</span>
                <b className="block text-[13.5px] font-bold truncate">{o.supplier_name} · {o.title}</b>
                <span className="block text-[12px] text-muted-foreground truncate">{projectLabel(o)}{money && o.amount_gross != null ? ` · ${amt(o)}` : ""}</span>
              </button>
            ))}
          </div>
        </section>
        {got.length > 0 && (
          <section className="glass rounded-[20px] p-3" data-testid="cal-got">
            <h3 className="px-1 text-[13px] font-extrabold">Převzato</h3>
            <p className="px-1 pb-2 text-[12px] text-muted-foreground">čeká na fakturu nebo platbu</p>
            {got.map((o) => (
              <button key={o.id} type="button" onClick={() => onOpen(o)} className="w-full flex items-center justify-between gap-2 px-1 py-1.5 text-left text-[13px] hover:text-brand">
                <span className="truncate">{o.supplier_name} · {o.title}</span>
                {money && o.amount_gross != null && <span className="tabular-nums font-semibold whitespace-nowrap">{amt(o)}</span>}
              </button>
            ))}
          </section>
        )}
      </aside>
    </div>
  );
}
